import { defineMessages, useIntl } from 'react-intl';

import type { AddressType } from '@sg/garnish';
import { switchcase } from '@sg/garnish';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

// ────────────────────────────────────────────────────────────────────────────────

export const useOrderStatusTitle = (params: UseOrderStatusTitleParams) => {
  const { orderType, orderStatus, isPast, isTrackable } = params;

  const { formatMessage } = useIntl();

  const isFailed = orderStatus === FlattenedOrderStatuses.Failed;
  const options = { order_type: orderType };

  // ─────────────────────────────────────────────────────────────────

  if (orderType === 'in-store') {
    return formatMessage(messages.titlePickupCompleted);
  }

  if (isFailed) return formatMessage(messages.titleFailed);

  if (isPast) return formatMessage(messages.titlePast);

  if (!isTrackable) return formatMessage(messages.titleUnavailable, options);

  if (orderType === 'pickup') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(messages.titlePickupReceived),
      [FlattenedOrderStatuses.Preparing]: formatMessage(messages.titlePickupPreparing),
      [FlattenedOrderStatuses.Completed]: formatMessage(messages.titlePickupCompleted),
    })(formatMessage(messages.titlePickupUnknown))(orderStatus ?? FlattenedOrderStatuses.Failed);
  }

  if (orderType === 'outpost') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(messages.titleOutpostReceived),
      [FlattenedOrderStatuses.Delivering]: formatMessage(messages.titleOutpostDelivering),
      [FlattenedOrderStatuses.Completed]: formatMessage(messages.titleOutpostCompleted),
    })(formatMessage(messages.titleOutpostUnknown))(orderStatus ?? FlattenedOrderStatuses.Failed);
  }

  if (orderType === 'delivery') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(messages.titleDeliveryReceived),
      [FlattenedOrderStatuses.Preparing]: formatMessage(messages.titleDeliveryPreparing),
      [FlattenedOrderStatuses.ReadyForCourier]: formatMessage(messages.titleDeliveryCourier),
      [FlattenedOrderStatuses.Delivering]: formatMessage(messages.titleDeliveryDelivering),
      [FlattenedOrderStatuses.Completed]: formatMessage(messages.titleDeliveryCompleted),
    })(formatMessage(messages.titleDeliveryUnknown))(orderStatus ?? FlattenedOrderStatuses.Failed);
  }

  return formatMessage(messages.unavailable);
};

// ─── Types ──────────────────────────────────────────────────────────────────────

export type UseOrderStatusTitleParams = {
  orderType?: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  isTrackable: boolean;
  isPast: boolean;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  titlePickupCompleted: {
    defaultMessage: 'Order completed',
    description: 'Order Status | Info | Title for completed pickup',
  },
  titleFailed: {
    defaultMessage: 'Order canceled',
    description: 'Order Status | Info | Title for failed order',
  },
  titlePast: {
    defaultMessage: 'Order completed',
    description: 'Order Status | Info | Title for past order',
  },
  titleUnavailable: {
    defaultMessage:
      '{order_type, select, pickup {Pickup order} outpost {Outpost order} delivery {Delivery order} other {}}',
    description: 'Order Status | Info | Title for unavailable order',
  },
  titlePickupReceived: {
    defaultMessage: 'Order received',
    description: 'Order Status | Info | Title for received pickup order',
  },
  titlePickupPreparing: {
    defaultMessage: 'Preparing order',
    description: 'Order Status | Info | Title for preparing pickup order',
  },
  titlePickupUnknown: {
    defaultMessage: 'Pickup order',
    description: 'Order Status | Info | Title for unknown pickup order',
  },
  titleOutpostReceived: {
    defaultMessage: 'Order received',
    description: 'Order Status | Info | Title for received outpost order',
  },
  titleOutpostDelivering: {
    defaultMessage: 'Order out for delivery',
    description: 'Order Status | Info | Title for delivering outpost order',
  },
  titleOutpostCompleted: {
    defaultMessage: 'Order completed',
    description: 'Order Status | Info | Title for completed outpost order',
  },
  titleOutpostUnknown: {
    defaultMessage: 'Outpost order',
    description: 'Order Status | Info | Title for unknown outpost order',
  },
  titleDeliveryReceived: {
    defaultMessage: 'Order received',
    description: 'Order Status | Info | Title for received delivery order',
  },
  titleDeliveryPreparing: {
    defaultMessage: 'Preparing order',
    description: 'Order Status | Info | Title for preparing delivery order',
  },
  titleDeliveryCourier: {
    defaultMessage: 'Order ready for courier',
    description: 'Order Status | Info | Title for ready for courier delivery order',
  },
  titleDeliveryDelivering: {
    defaultMessage: 'Order out for delivery',
    description: 'Order Status | Info | Title for delivering delivery order',
  },
  titleDeliveryCompleted: {
    defaultMessage: 'Order completed',
    description: 'Order Status | Info | Title for completed delivery order',
  },
  titleDeliveryUnknown: {
    defaultMessage: 'Delivery order',
    description: 'Order Status | Info | Title for unknown delivery order',
  },
  unavailable: {
    defaultMessage: 'Unavailable',
    description: 'Order Status | Info | Unavailable fallback',
  },
});
